import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  ImageIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import type { FolderColor, Page, PageKind } from '@regia/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { errorMessage } from '../../lib/api';
import { Button, ConfirmDialog, Field, Input, Section, Segmented, Textarea } from '../../ui';
import { useDeletePage, useRemoveLogo, useUpdatePage, useUploadLogo } from './api';
import { ChannelsEditor } from './ChannelsEditor';
import { ColorPicker } from './ColorPicker';

interface Form {
  name: string;
  kind: PageKind;
  color: FolderColor;
  description: string;
  audience: string;
  tone: string;
  website: string;
  phone: string;
  address: string;
  notes: string;
}

const fromPage = (p: Page): Form => ({
  name: p.name,
  kind: p.kind,
  color: p.color,
  description: p.description,
  audience: p.audience,
  tone: p.tone,
  website: p.website,
  phone: p.phone,
  address: p.address,
  notes: p.notes,
});

export function SchedaInfo({ page }: { page: Page }) {
  const navigate = useNavigate();
  const update = useUpdatePage(page.id);
  const uploadLogo = useUploadLogo(page.id);
  const removeLogo = useRemoveLogo(page.id);
  const del = useDeletePage(page.id);
  const [form, setForm] = useState<Form>(() => fromPage(page));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<'delete' | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setForm(fromPage(page));
  }, [page]);

  const dirty = JSON.stringify(form) !== JSON.stringify(fromPage(page));
  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    try {
      await update.mutateAsync(form);
      toast.success('Scheda salvata');
    } catch (err) {
      setErrors((err as { fields?: Record<string, string> }).fields ?? { form: errorMessage(err) });
    }
  }

  async function onLogo(file: File | undefined) {
    if (!file) return;
    try {
      await uploadLogo.mutateAsync(file);
      toast.success('Logo caricato');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function toggleArchive() {
    try {
      await update.mutateAsync({ archived: !page.archived });
      toast.success(page.archived ? 'Scheda ripristinata' : 'Scheda archiviata');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function doDelete() {
    try {
      await del.mutateAsync();
      toast.success('Scheda eliminata');
      navigate('/schede', { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  const text = (
    key: keyof Form,
    label: string,
    opts: { hint?: string; area?: boolean; type?: string; placeholder?: string } = {},
  ) => (
    <Field label={label} hint={opts.hint} error={errors[key]} optional={key !== 'name'}>
      {(id, describedBy) =>
        opts.area ? (
          <Textarea
            id={id}
            value={String(form[key])}
            onChange={(e) => set(key, e.target.value as never)}
            aria-describedby={describedBy}
            placeholder={opts.placeholder}
            invalid={Boolean(errors[key])}
          />
        ) : (
          <Input
            id={id}
            type={opts.type ?? 'text'}
            value={String(form[key])}
            onChange={(e) => set(key, e.target.value as never)}
            aria-describedby={describedBy}
            placeholder={opts.placeholder}
            invalid={Boolean(errors[key])}
          />
        )
      }
    </Field>
  );

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-x-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <form onSubmit={save} noValidate className="pb-24 lg:pb-0">
        <Section title="Informazioni">
          <div className="flex flex-col gap-4 rounded-[var(--radius-sheet)] bg-sheet p-5">
            {text('name', 'Nome')}
            <div className="flex flex-col gap-2">
              <span className="text-sm font-semibold text-ink">Di chi è</span>
              <Segmented
                label="Di chi è"
                value={form.kind}
                onChange={(v) => set('kind', v)}
                options={[
                  { value: 'cliente', label: 'Di un cliente' },
                  { value: 'mio', label: 'Mia' },
                ]}
              />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-semibold text-ink">Colore della cartellina</span>
              <ColorPicker value={form.color} onChange={(c) => set('color', c)} />
            </div>
            {text('description', 'Descrizione', {
              area: true,
              placeholder: 'Cosa fa, dove si trova, cosa la rende speciale',
            })}
          </div>
        </Section>
        <Section title="Per l’AI">
          <div className="flex flex-col gap-4 rounded-[var(--radius-sheet)] bg-sheet p-5">
            {text('audience', 'Pubblico', {
              area: true,
              hint: 'A chi parla: età, zona, interessi',
              placeholder: 'Donne 30-55 anni di Settimo e Torino nord',
            })}
            {text('tone', 'Tono di voce', {
              area: true,
              hint: 'Come scrive: formale, amichevole, ironico…',
              placeholder: 'Caldo e rassicurante, dà del tu',
            })}
          </div>
        </Section>
        <Section title="Contatti">
          <div className="flex flex-col gap-4 rounded-[var(--radius-sheet)] bg-sheet p-5">
            {text('website', 'Sito', { type: 'url', placeholder: 'https://' })}
            {text('phone', 'Telefono', { type: 'tel' })}
            {text('address', 'Indirizzo')}
            {text('notes', 'Note', { area: true })}
          </div>
        </Section>
        {errors.form ? <p className="mb-4 text-sm text-alert">{errors.form}</p> : null}
        <div
          className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 border-t border-line bg-sheet/95 px-4 py-3 backdrop-blur-md transition-transform lg:static lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none data-[hidden=true]:translate-y-[200%] lg:data-[hidden=true]:translate-y-0"
          data-hidden={!dirty}
          inert={!dirty}
        >
          <div className="mx-auto flex max-w-xl gap-2 lg:mx-0">
            <Button
              variant="secondary"
              onClick={() => setForm(fromPage(page))}
              disabled={!dirty}
              className="flex-1 lg:flex-none"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              loading={update.isPending}
              disabled={!dirty}
              className="flex-[2] lg:flex-none"
            >
              Salva modifiche
            </Button>
          </div>
        </div>
      </form>

      <div>
        <Section title="Logo">
          <div className="flex items-center gap-4 rounded-[var(--radius-sheet)] bg-sheet p-5">
            {page.logoUrl ? (
              <img
                src={page.logoUrl}
                alt="Logo"
                className="h-16 w-16 rounded-full object-cover ring-1 ring-line"
              />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sheet-2 text-ink-3">
                <ImageIcon size={26} aria-hidden />
              </span>
            )}
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onLogo(e.target.files?.[0])}
              />
              <Button
                variant="secondary"
                size="sm"
                loading={uploadLogo.isPending}
                onClick={() => fileRef.current?.click()}
              >
                {page.logoUrl ? 'Cambia' : 'Carica logo'}
              </Button>
              {page.logoUrl ? (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={removeLogo.isPending}
                  onClick={() => removeLogo.mutate()}
                >
                  Togli
                </Button>
              ) : null}
            </div>
          </div>
        </Section>
        <ChannelsEditor page={page} />
        <Section title="Archivio">
          <div className="flex flex-col gap-3 rounded-[var(--radius-sheet)] bg-sheet p-5">
            <p className="text-sm text-ink-2">
              {page.archived
                ? 'La scheda è archiviata: non compare in Oggi e non riceve controlli.'
                : 'Archivia una scheda che non segui più: resta tutto, puoi ripristinarla quando vuoi.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                icon={page.archived ? ArrowCounterClockwiseIcon : ArchiveIcon}
                onClick={toggleArchive}
                loading={update.isPending && !dirty}
              >
                {page.archived ? 'Ripristina' : 'Archivia'}
              </Button>
              {page.archived ? (
                <Button
                  variant="ghost"
                  icon={TrashIcon}
                  className="text-alert"
                  onClick={() => setConfirm('delete')}
                >
                  Elimina per sempre
                </Button>
              ) : null}
            </div>
          </div>
        </Section>
      </div>

      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Eliminare ${page.name}?`}
        message="Si cancellano la scheda e tutto quello che contiene: contenuti, accessi, attività e numeri. Non si può annullare."
        confirmLabel="Elimina per sempre"
        danger
        loading={del.isPending}
        onConfirm={doDelete}
      />
    </div>
  );
}
